import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Badge, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import Chart from 'react-apexcharts';
import * as Icon from 'react-feather';
import { MEXICO_CITY_TIME_ZONE } from '../utils/dateTime';
import './Usage.css';

const Usage = () => {
    const [billingData, setBillingData] = useState(null);
    const [monthlyStats, setMonthlyStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const authHeader = useAuthHeader();
    const authUser = useAuthUser();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_URL;

            // Fetch billing data
            const billingResponse = await axios({
                method: 'GET',
                url: `${apiUrl}/billing/usage`,
                headers: {
                    Authorization: authHeader()
                }
            });

            setBillingData(billingResponse.data);

            // Fetch monthly statistics
            const statsResponse = await axios({
                method: 'GET',
                url: `${apiUrl}/trips/statistics/monthly?months=6`,
                headers: {
                    Authorization: authHeader()
                }
            });

            setMonthlyStats(statsResponse.data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('No se pudo cargar la información de facturación');
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-MX', {
            timeZone: MEXICO_CITY_TIME_ZONE,
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const calculateDaysRemaining = (trialEnd) => {
        if (!trialEnd) return 0;
        const now = new Date();
        const end = new Date(trialEnd);
        const diffTime = end - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    };

    if (loading) {
        return (
            <Container className="mt-5 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Cargando información de facturación...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    const isInTrial = billingData?.is_trial && billingData?.trial_end;
    const daysRemaining = isInTrial ? calculateDaysRemaining(billingData.trial_end) : 0;

    // Use real data from API if available, otherwise use empty arrays
    const months = monthlyStats?.data ? monthlyStats.data.map(d => d.month_name) : [];
    const trips = monthlyStats?.data ? monthlyStats.data.map(d => d.trip_count) : [];
    const amounts = monthlyStats?.data ? monthlyStats.data.map(d => d.amount) : [];

    // ApexCharts configuration for Trips Line Chart
    const tripsChartOptions = {
        chart: {
            type: 'line',
            toolbar: {
                show: false
            }
        },
        stroke: {
            curve: 'smooth',
            width: 3
        },
        colors: ['#4bc0c0'],
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories: months,
            labels: {
                style: {
                    fontSize: '12px'
                }
            }
        },
        yaxis: {
            min: 0,
            labels: {
                style: {
                    fontSize: '12px'
                }
            }
        },
        tooltip: {
            theme: 'light'
        },
        legend: {
            show: true,
            position: 'top'
        }
    };

    const tripsChartSeries = [{
        name: 'Viajes Registrados',
        data: trips
    }];

    // ApexCharts configuration for Billing Bar Chart
    const billingChartOptions = {
        chart: {
            type: 'bar',
            toolbar: {
                show: false
            }
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                columnWidth: '60%'
            }
        },
        colors: ['#36a2eb'],
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories: months,
            labels: {
                style: {
                    fontSize: '12px'
                }
            }
        },
        yaxis: {
            min: 0,
            labels: {
                formatter: (value) => formatCurrency(value),
                style: {
                    fontSize: '12px'
                }
            }
        },
        tooltip: {
            theme: 'light',
            y: {
                formatter: (value) => formatCurrency(value)
            }
        },
        legend: {
            show: true,
            position: 'top'
        }
    };

    const billingChartSeries = [{
        name: 'Facturación (MXN)',
        data: amounts
    }];

    return (
        <Container fluid className="usage-page mt-4">
            <Row className="mb-4">
                <Col>
                    <h2 className="page-title">
                        <Icon.BarChart2 className="me-2" />
                        Uso y Facturación
                    </h2>
                </Col>
            </Row>

            {/* Trial Status Alert */}
            {isInTrial && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="success" className="trial-alert">
                            <div className="d-flex align-items-center">
                                <Icon.Gift size={32} className="me-3" />
                                <div>
                                    <h5 className="mb-1">🎉 Periodo de Prueba Gratuito Activo</h5>
                                    <p className="mb-0">
                                        Te quedan <strong>{daysRemaining} días</strong> de acceso gratuito.
                                        La facturación comenzará el <strong>{formatDate(billingData.trial_end)}</strong>
                                    </p>
                                </div>
                            </div>
                        </Alert>
                    </Col>
                </Row>
            )}

            {/* Current Period Stats */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="stat-card">
                        <Card.Body>
                            <div className="stat-icon trips">
                                <Icon.TrendingUp size={24} />
                            </div>
                            <div className="stat-content">
                                <p className="stat-label">Viajes Este Mes</p>
                                <h3 className="stat-value">{billingData?.current_period_trips || 0}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={3}>
                    <Card className="stat-card">
                        <Card.Body>
                            <div className="stat-icon amount">
                                <Icon.DollarSign size={24} />
                            </div>
                            <div className="stat-content">
                                <p className="stat-label">Monto Actual</p>
                                <h3 className="stat-value">{formatCurrency(billingData?.current_period_amount)}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={3}>
                    <Card className="stat-card">
                        <Card.Body>
                            <div className="stat-icon price">
                                <Icon.Tag size={24} />
                            </div>
                            <div className="stat-content">
                                <p className="stat-label">Precio por Viaje</p>
                                <h3 className="stat-value">{formatCurrency(billingData?.price_per_trip)}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={3}>
                    <Card className="stat-card">
                        <Card.Body>
                            <div className="stat-icon status">
                                <Icon.CheckCircle size={24} />
                            </div>
                            <div className="stat-content">
                                <p className="stat-label">Estado</p>
                                <h3 className="stat-value">
                                    <Badge bg={isInTrial ? 'success' : 'primary'}>
                                        {isInTrial ? 'Prueba' : 'Activo'}
                                    </Badge>
                                </h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Current Billing Period Details */}
            <Row className="mb-4">
                <Col lg={6}>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">
                                <Icon.Calendar className="me-2" />
                                Período de Facturación Actual
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="billing-detail-item">
                                <span className="label">Inicio del Período:</span>
                                <span className="value">{formatDate(billingData?.current_period_start)}</span>
                            </div>
                            <div className="billing-detail-item">
                                <span className="label">Fin del Período:</span>
                                <span className="value">{formatDate(billingData?.current_period_end)}</span>
                            </div>
                            <div className="billing-detail-item">
                                <span className="label">Próxima Fecha de Facturación:</span>
                                <span className="value">{formatDate(billingData?.next_billing_date)}</span>
                            </div>
                            <hr />
                            <div className="billing-detail-item total">
                                <span className="label"><strong>Total a Pagar:</strong></span>
                                <span className="value"><strong>{formatCurrency(billingData?.current_period_amount)}</strong></span>
                            </div>
                            {isInTrial && (
                                <Alert variant="info" className="mt-3 mb-0">
                                    <small>
                                        <Icon.Info size={16} className="me-1" />
                                        Durante el período de prueba no se realizarán cobros.
                                    </small>
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={6}>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">
                                <Icon.TrendingUp className="me-2" />
                                Totales Históricos
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="billing-detail-item">
                                <span className="label">Total de Viajes Facturados:</span>
                                <span className="value">{billingData?.total_trips_billed || 0} viajes</span>
                            </div>
                            <div className="billing-detail-item">
                                <span className="label">Total Facturado Histórico:</span>
                                <span className="value">{formatCurrency(billingData?.total_amount_billed)}</span>
                            </div>
                            <div className="billing-detail-item">
                                <span className="label">Última Fecha de Facturación:</span>
                                <span className="value">{formatDate(billingData?.last_billing_date) || 'N/A'}</span>
                            </div>
                            <hr />
                            <div className="billing-detail-item">
                                <span className="label">Moneda:</span>
                                <span className="value">{billingData?.currency || 'MXN'}</span>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Charts */}
            <Row className="mb-4">
                <Col lg={6}>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">
                                <Icon.TrendingUp className="me-2" />
                                Viajes por Mes
                            </h5>
                        </Card.Header>
                        <Card.Body style={{ height: '350px' }}>
                            <Chart
                                options={tripsChartOptions}
                                series={tripsChartSeries}
                                type="line"
                                height="320"
                            />
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={6}>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">
                                <Icon.DollarSign className="me-2" />
                                Facturación Mensual
                            </h5>
                        </Card.Header>
                        <Card.Body style={{ height: '350px' }}>
                            <Chart
                                options={billingChartOptions}
                                series={billingChartSeries}
                                type="bar"
                                height="320"
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Pricing Info */}
            <Row>
                <Col>
                    <Card className="pricing-info-card">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={8}>
                                    <h5>
                                        <Icon.Info className="me-2" />
                                        Modelo de Pago por Uso
                                    </h5>
                                    <p className="mb-0">
                                        Pagas solo por los viajes que registras en el sistema. Sin cargos ocultos, sin límites de usuarios o unidades.
                                        La facturación se realiza mensualmente basada en tu uso real.
                                    </p>
                                </Col>
                                <Col md={4} className="text-center">
                                    <div className="price-highlight">
                                        <span className="price-amount">{formatCurrency(billingData?.price_per_trip)}</span>
                                        <span className="price-label">por viaje</span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Usage;
